import React, { useEffect, useState } from 'react';
import { Button, Form, Input, Modal, Typography, message, Space } from 'antd';
import { changeAccountPassword, getCurrentAdmin, updateAccountUsername, logout, type AdminUser } from '../../utils/api';

const AdminAccountEditPage: React.FC = () => {
  const [me, setMe] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modals state
  const [openUsernameModal, setOpenUsernameModal] = useState(false);
  const [openPasswordModal, setOpenPasswordModal] = useState(false);

  // Username form
  const [usernameForm] = Form.useForm();
  const [submittingUsername, setSubmittingUsername] = useState(false);

  // Password form
  const [passwordForm] = Form.useForm();
  const [submittingPassword, setSubmittingPassword] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const user = await getCurrentAdmin();
        if (mounted) {
          setMe(user);
          // Seed username form
          usernameForm.setFieldsValue({ currentUsername: user.username, newUsername: user.username });
        }
      } catch (e: any) {
        if (mounted) setError(e?.response?.data?.error || 'アカウント情報の取得に失敗しました');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [usernameForm]);

  const submitUsername = async () => {
    setSubmittingUsername(true);
    try {
      const vals = await usernameForm.validateFields();
      const updated = await updateAccountUsername({ newUsername: vals.newUsername, password: vals.password });
      // Backend now sends verification email and keeps current username until confirmed
      setMe((prev: AdminUser | null) => (prev ? { ...prev, username: updated.username } : updated));
      message.success('確認メールを送信しました。メールのリンクから確認してください。');
      setOpenUsernameModal(false);
      // Immediately logout to enforce re-login after verification
      logout();
    } catch (err: any) {
      const msg = err?.response?.data?.error || 'アカウントIDの更新に失敗しました';
      message.error(msg);
    } finally {
      setSubmittingUsername(false);
    }
  };

  const submitPassword = async () => {
    setSubmittingPassword(true);
    try {
      const vals = await passwordForm.validateFields();
      await changeAccountPassword({ oldPassword: vals.oldPassword, newPassword: vals.newPassword });
      message.success('パスワードを変更しました');
      setOpenPasswordModal(false);
      passwordForm.resetFields();
    } catch (err: any) {
      const msg = err?.response?.data?.error || 'パスワードの変更に失敗しました';
      message.error(msg);
    } finally {
      setSubmittingPassword(false);
    }
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div className="text-red-600">{error}</div>;

  return (
    <div className="max-w-3xl mx-auto">
      <Typography.Title level={3} style={{ marginTop: 0, marginBottom: 16 }}>ログイン情報変更</Typography.Title>

      <Typography.Title level={5} style={{ marginTop: 8, marginBottom: 8, textAlign: 'left' }}>現在のアカウントID</Typography.Title>
      <div className="relative bg-[#F4F6F9] rounded-lg p-4 mb-8 w-[calc(100%-90px)]">
        <div className="text-sm font-medium text-left pr-24">{me?.username}</div>
        <Button className="!absolute right-4 top-1/2 -translate-y-1/2 right-[-90px]" type="default" onClick={() => setOpenUsernameModal(true)}>編集</Button>
      </div>

      <Typography.Title level={5} style={{ marginTop: 8, marginBottom: 8, textAlign: 'left' }}>パスワードを変更</Typography.Title>
      <div className="relative bg-[#F4F6F9] rounded-lg p-4 mb-8 w-[calc(100%-90px)]">
        <div className="text-sm font-medium text-left pr-24">アカウントを保護する</div>
        <Button className="!absolute right-4 top-1/2 -translate-y-1/2 right-[-90px]" type="default" onClick={() => setOpenPasswordModal(true)}>編集</Button>
      </div>

      {/* Update Username Modal */}
      <Modal
        title="アカウントIDを変更"
        open={openUsernameModal}
        onCancel={() => setOpenUsernameModal(false)}
        footer={null}
        destroyOnClose
      >
        <Form form={usernameForm} layout="vertical" onFinish={submitUsername} initialValues={{ currentUsername: me?.username, newUsername: me?.username }}>
          <Form.Item label="現在のアカウントID" name="currentUsername">
            <Input disabled />
          </Form.Item>
          <Form.Item label="新しいアカウントID" name="newUsername" rules={[{ required: true, message: '新しいアカウントIDを入力してください' }]}>
            <Input autoFocus />
          </Form.Item>
          <Form.Item label="確認のため現在のパスワードを入力してください" name="password" rules={[{ required: true, message: 'パスワードを入力してください' }]}>
            <Input.Password />
          </Form.Item>
          <div className="text-sm text-gray-600 mb-3">メールアドレスの変更は、送信された確認メールのリンクをクリックすると反映されます。</div>
          <Space style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={() => setOpenUsernameModal(false)}>閉じる</Button>
            <Button type="primary" htmlType="submit" loading={submittingUsername}>保存</Button>
          </Space>
        </Form>
      </Modal>

      {/* Change Password Modal */}
      <Modal
        title="パスワードを変更"
        open={openPasswordModal}
        onCancel={() => setOpenPasswordModal(false)}
        footer={null}
        destroyOnClose
      >
        <Form form={passwordForm} layout="vertical" onFinish={submitPassword}>
          <Form.Item label="現在のパスワード" name="oldPassword" rules={[{ required: true, message: '現在のパスワードを入力してください' }]}>
            <Input.Password />
          </Form.Item>
          <Form.Item label="新しいパスワード" name="newPassword" rules={[{ required: true, message: '新しいパスワードを入力してください' }, { min: 6, message: '6文字以上で入力してください' }]}>
            <Input.Password />
          </Form.Item>
          <Form.Item
            label="新しいパスワード（確認用）"
            name="confirmPassword"
            dependencies={["newPassword"]}
            rules={[
              { required: true, message: '新しいパスワードを確認してください' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('パスワードが一致しません'));
                },
              }),
            ]}
          >
            <Input.Password />
          </Form.Item>
          <Space style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={() => setOpenPasswordModal(false)}>閉じる</Button>
            <Button type="primary" htmlType="submit" loading={submittingPassword}>保存</Button>
          </Space>
        </Form>
      </Modal>
    </div>
  );
};

export default AdminAccountEditPage;
